import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { formatCountdown, formatDropTime, formatVnd } from "../../shared/i18n/index.ts";
import { PRICE_STEP } from "../../shared/limits.ts";
import { PriceDisplay } from "../components/PriceDisplay.tsx";
import { api } from "../lib/api.ts";
import { serverNow } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { loadOwner, type OwnerListing } from "../lib/listing-form.ts";
import { useMe } from "../lib/me.tsx";
import { ownerSegment } from "../lib/owner.ts";

/** /me/listings/:id — owner view (08-design §Listing, owner adds countdown, range bar, floor, cancel). */
export function ListingOwner() {
  const { id = "" } = useParams();
  const { t, locale } = useI18n();
  const { me, loading } = useMe();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [l, setL] = useState<OwnerListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [floor, setFloor] = useState<string>("");
  const [, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    loadOwner(id)
      .then((o) => live && setL(o))
      .catch(() => live && setError(t("error.not_found")));
    const tick = setInterval(() => setTick((n) => n + 1), 1000);
    return () => {
      live = false;
      clearInterval(tick);
    };
  }, [id, t]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to={`/login?next=/me/listings/${id}`} replace />;
  if (error) return <div className="page muted">{error}</div>;
  if (!l) return <div className="page muted">{t("common.loading")}</div>;
  const now = serverNow();
  const seg = ownerSegment({ ...l, cover: null }, now);
  const published = params.get("published");

  async function cancel() {
    try {
      await api(`/api/listings/${id}/cancel`, { body: {} });
      navigate("/me/listings?status=ended", { replace: true });
    } catch {
      setError(t("error.generic"));
    }
  }

  async function lower() {
    const v = Number(floor);
    if (!Number.isFinite(v)) return;
    try {
      await api(`/api/listings/${id}/floor`, {
        body: { floor_price: Math.max(0, Math.round(v / PRICE_STEP) * PRICE_STEP) },
      });
      setL({ ...l!, floor_price: Math.max(0, Math.round(v / PRICE_STEP) * PRICE_STEP) });
      setFloor("");
    } catch {
      setError(t("error.generic"));
    }
  }

  async function relist() {
    const r = await api<{ id: string }>(`/api/listings/${id}/relist`, { body: {} }).catch(
      () => null,
    );
    if (r) navigate(`/sell/${r.id}`);
  }

  const range =
    l.start_price && seg
      ? Math.round(((l.start_price - seg.price) / Math.max(1, l.start_price - l.floor_price)) * 100)
      : 0;

  return (
    <div className="page page--narrow" data-testid="listing-owner">
      {published ? (
        <p className="ok" role="status">
          {published === "scheduled"
            ? t("sell.published.drop", {
                when: l.start_at ? formatDropTime(l.start_at, locale, now) : "",
              })
            : t("sell.published.now")}
        </p>
      ) : null}
      {l.images[0] ? <img className="hero" src={l.images[0].url} alt="" /> : null}
      <h1 className="h1">{l.title}</h1>
      <p className="muted">
        {t(`listing.status.${l.status}`)} · {l.quantity - l.quantity_remaining}/{l.quantity} · 👁{" "}
        {l.watch_count} · 🔔 {l.alert_count}
      </p>
      {seg && l.status === "active" ? (
        <>
          <PriceDisplay
            segment={seg}
            size="lg"
            {...(l.start_price ? { startPrice: l.start_price } : {})}
          />
          <p className="countdown" data-testid="countdown">
            {l.end_at ? formatCountdown(l.end_at - now, locale) : ""}
          </p>
          <div className="rangebar" aria-hidden="true">
            <span
              className="rangebar__fill"
              style={{ width: `${Math.min(100, Math.max(0, range))}%` }}
            />
          </div>
          <p className="hint">
            {formatVnd(l.start_price ?? 0)} → {l.floor_price > 0 ? formatVnd(l.floor_price) : "0 ₫"}
          </p>
        </>
      ) : null}
      {l.status === "scheduled" && l.start_at ? (
        <p>{t("listing.starts_at_drop", { when: formatDropTime(l.start_at, locale, now) })}</p>
      ) : null}
      <section className="section">
        <h2 className="h2">{t("sell.ladder")}</h2>
        {l.ladder ? (
          <div className="ladder" data-testid="ladder">
            <p className="muted small">{t("sell.ladder.hint")}</p>
            {l.ladder.buckets.map((b) => (
              <div key={b.from} className="ladder__row">
                <span className="ladder__label">{formatVnd(b.from)}</span>
                <span
                  className="ladder__bar"
                  style={{ width: `${Math.round((b.count / l.ladder!.total) * 100)}%` }}
                />
                <span className="ladder__count">{b.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted small">{t("sell.ladder.min")}</p>
        )}
      </section>
      {l.status === "active" || l.status === "scheduled" ? (
        <section className="section">
          <h2 className="h2">{t("sell.lower_floor")}</h2>
          <p className="muted small">
            {t("price.floor")}: {formatVnd(l.floor_price)}
          </p>
          <div className="row">
            <input
              className="input"
              type="number"
              inputMode="numeric"
              step={PRICE_STEP}
              min={0}
              max={Math.max(0, l.floor_price - PRICE_STEP)}
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
            />
            <button
              className="btn"
              disabled={!floor || Number(floor) >= l.floor_price}
              onClick={() => void lower()}
            >
              {t("common.save")}
            </button>
          </div>
          <div className="stack" style={{ marginTop: 16 }}>
            {!confirmCancel ? (
              <button
                className="btn btn--danger"
                onClick={() => setConfirmCancel(true)}
                data-testid="cancel-open"
              >
                {t("sell.cancel_listing")}
              </button>
            ) : (
              <div className="sheet">
                <p>{t("sell.cancel_listing.confirm")}</p>
                <div className="row">
                  <button
                    className="btn btn--danger"
                    onClick={() => void cancel()}
                    data-testid="cancel-confirm"
                  >
                    {t("sell.cancel_listing")}
                  </button>
                  <button className="btn btn--ghost" onClick={() => setConfirmCancel(false)}>
                    {t("common.back")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      ) : (
        <section className="section">
          <button className="btn" onClick={() => void relist()}>
            {t("sell.relist")}
          </button>
        </section>
      )}
      <p>
        <Link to="/me/listings" className="btn btn--ghost">
          {t("common.back")}
        </Link>
      </p>
    </div>
  );
}
